import { AuthService } from './auth'

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://dashboard.quali-d.com/api'
  : '/api'

export class DashboardService {
  // Helper method to parse JSON response regardless of content-type
  static async parseJsonResponse (response) {
    let text
    try {
      text = await response.text()
      return JSON.parse(text)
    } catch (parseError) {
      const contentType = response.headers.get('content-type')
      console.error('Failed to parse JSON response:', parseError)
      console.error('Content-Type:', contentType)
      console.error('Response text:', text)
      throw new Error('Invalid JSON response')
    }
  }

  static async getTopCustomerServiceAgent () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${API_BASE_URL}/top_performer_this_month.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ token: token })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text()
        console.error('Non-JSON response received:', textResponse)
        throw new Error('Invalid response format - expected JSON')
      }

      const data = await response.json()
      return {
        bestPerformerName: data.best_performer_name || 'N/A'
      }
    } catch (error) {
      console.error('Error fetching top customer service agent:', error)
      return {
        bestPerformerName: 'Error loading',
        error: error.message
      }
    }
  }

  static async getTopSalesAgent () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${API_BASE_URL}/top_sales_agent_performer_this_month.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ token: token })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text()
        console.error('Non-JSON response received:', textResponse)
        throw new Error('Invalid response format - expected JSON')
      }

      const data = await response.json()
      return {
        bestPerformerName: data.best_performer_name || 'N/A'
      }
    } catch (error) {
      console.error('Error fetching top sales agent:', error)
      return {
        bestPerformerName: 'Error loading',
        error: error.message
      }
    }
  }

  static async getQualiBotStatus () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-bot/status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        qualibotEnabled: data.qualibot === 1,
        status: data.status || 'UNKNOWN',
        colorCode: data.color_code || '#6b7280',
        block_chatbot: data.block_chatbot || 0
      }
    } catch (error) {
      console.error('Error fetching QualiBot status:', error)
      return {
        qualibotEnabled: false,
        status: 'ERROR',
        colorCode: '#ef4444',
        error: error.message
      }
    }
  }

  static async getActiveAgents () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const url = `${API_BASE_URL}/get_all_active_agents.php?token=${encodeURIComponent(token)}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)

      return {
        activeAgentsCount: data.active_agents_count || 0,
        offlineAgentsCount: data.offline_agents_count || 0,
        totalAgents: (data.active_agents_count || 0) + (data.offline_agents_count || 0),
        activeAgents: data.active_agents || [],
        offlineAgents: data.offline_agents || [],
        message: data.message || ''
      }
    } catch (error) {
      console.error('Error fetching active agents:', error)
      return {
        activeAgentsCount: 0,
        offlineAgentsCount: 0,
        totalAgents: 0,
        activeAgents: [],
        offlineAgents: [],
        message: '',
        error: error.message
      }
    }
  }

  static async getAgents (agentRoleId = 1) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${API_BASE_URL}/get_my_agents.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          token: token,
          agent_role_id: agentRoleId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return data || []
    } catch (error) {
      console.error('Error fetching agents:', error)
      return []
    }
  }

  static async generateAgentReport (agentId, startDate, endDate) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Try multiple possible API endpoints
      const possibleEndpoints = [
        `${API_BASE_URL}/export_agent_performance.php`,
        `${API_BASE_URL}/agent_report.php`,
        `${API_BASE_URL}/generate_agent_report.php`,
        'https://api.quali-d.com/agents/export-agent-performance'
      ]

      let lastError = null

      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`Trying endpoint: ${endpoint}`)

          const requestBody = {
            token: token,
            agent_id: agentId,
            start_date: startDate,
            end_date: endDate
          }

          // For the external API, use Bearer token format
          const headers = endpoint.includes('api.quali-d.com')
            ? {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
              }
            : {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              }

          const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            mode: 'cors',
            body: JSON.stringify(requestBody)
          })

          if (response.ok) {
            const data = await this.parseJsonResponse(response)
            return {
              success: true,
              message: data.message || 'Report generated successfully',
              url: data.url,
              endpoint: endpoint
            }
          }

          console.log(`Endpoint ${endpoint} returned status: ${response.status}`)
          lastError = new Error(`HTTP error! status: ${response.status} from ${endpoint}`)
        } catch (error) {
          console.log(`Endpoint ${endpoint} failed:`, error.message)
          lastError = error
          continue
        }
      }

      throw lastError || new Error('All API endpoints failed')
    } catch (error) {
      console.error('Error generating agent report:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async addAgent (name, departmentId, agentRoleId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/agents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          name: name,
          department: departmentId,
          agent_role_id: agentRoleId
        })
      })

      const data = await this.parseJsonResponse(response)

      if (!response.ok) {
        // Return error message from API response
        return {
          success: false,
          message: data.message || `HTTP error! status: ${response.status}`,
          error: data.message || `HTTP error! status: ${response.status}`
        }
      }

      return {
        success: data.success || true,
        message: data.message || 'Agent created successfully',
        agent: data.agent || data.data || {},
        data: data
      }
    } catch (error) {
      console.error('Error adding agent:', error)
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        error: error.message
      }
    }
  }

  static async editAgent (agentId, name, departmentId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${API_BASE_URL}/edit_agent.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          agent_id: agentId,
          token: token,
          name: name,
          department_id: departmentId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'Agent updated successfully',
        data: data
      }
    } catch (error) {
      console.error('Error editing agent:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async deleteAgent (agentId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`${API_BASE_URL}/delete_agent.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          token: token,
          agent_id: agentId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'Agent deleted successfully',
        data: data
      }
    } catch (error) {
      console.error('Error deleting agent:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async getDepartments () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/departments', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        departments: data.departments || []
      }
    } catch (error) {
      console.error('Error fetching departments:', error)
      return {
        success: false,
        departments: [],
        error: error.message
      }
    }
  }

  static async addDepartment (name) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/departments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          name: name
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Department added successfully',
        departmentId: data.department_id
      }
    } catch (error) {
      console.error('Error adding department:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async editDepartment (departmentId, name) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/departments/${departmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          name: name
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Department updated successfully'
      }
    } catch (error) {
      console.error('Error editing department:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async deleteDepartment (departmentId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/departments/${departmentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Department deleted successfully'
      }
    } catch (error) {
      console.error('Error deleting department:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async getConversations () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/internal-messaging/conversations', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        conversations: data.conversations || []
      }
    } catch (error) {
      console.error('Error fetching conversations:', error)
      return {
        success: false,
        conversations: [],
        error: error.message
      }
    }
  }

  static async getConversation (agentId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/internal-messaging/conversation?contact_type=agent&contact_id=${agentId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        conversation: data || {}
      }
    } catch (error) {
      console.error('Error fetching conversation:', error)
      return {
        success: false,
        conversation: {},
        error: error.message
      }
    }
  }

  static async sendMessage (recipientId, message) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/internal-messaging/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          to: 'agent',
          recipient_id: recipientId,
          message: message
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || {}
      }
    } catch (error) {
      console.error('Error sending message:', error)
      return {
        success: false,
        message: {},
        error: error.message
      }
    }
  }

  static async getWhatsAppConversations (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()

      // Always add required params
      queryParams.set('page', params.page || 1)
      queryParams.set('archived', params.archived !== undefined ? params.archived : 0)
      queryParams.set('agent_id', params.agent_id || 'null')
      queryParams.set('is_agent', params.is_agent !== undefined ? params.is_agent : false)
      queryParams.set('search', params.search || '')

      // Only add needs_agent if it has a specific value
      if (params.needs_agent !== undefined && params.needs_agent !== '' && params.needs_agent !== null) {
        queryParams.set('needs_agent', params.needs_agent)
      }

      // Only add department_id if it has a specific value (for filtering by department)
      if (params.department_id !== undefined && params.department_id !== '' && params.department_id !== null) {
        queryParams.set('department_id', params.department_id)
      }

      // Only add unread_only if it has a specific value (for filtering unread conversations)
      if (params.unread_only !== undefined && params.unread_only !== '' && params.unread_only !== null && params.unread_only !== 0) {
        queryParams.set('unread_only', params.unread_only)
      }

      // Add timestamp to prevent caching
      queryParams.set('_t', Date.now())

      console.log('WhatsApp API Request URL:', `https://api.quali-d.com/whatsapp-conversations?${queryParams}`)
      const response = await fetch(`https://api.quali-d.com/whatsapp-conversations?${queryParams}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        messages: data.messages || [],
        lastPage: data.last_page || 1,
        message: data.message || 'Data retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching WhatsApp conversations:', error)
      return {
        success: false,
        messages: [],
        lastPage: 1,
        error: error.message
      }
    }
  }

  static async getWhatsAppConversationMessages (waId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/whatsapp-conversation/get?wa_id=${encodeURIComponent(waId)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        messages: data.messages || [],
        message: data.message || 'Data retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching WhatsApp conversation messages:', error)
      return {
        success: false,
        messages: [],
        error: error.message
      }
    }
  }

  static async getWhatsAppUnreadCount () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/whatsapp/unread-count', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        unreadConversations: data.unread_conversations || 0,
        totalConversations: data.total_conversations || 0,
        readConversations: data.read_conversations || 0,
        message: data.message || 'Unread count retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching WhatsApp unread count:', error)
      return {
        success: false,
        unreadConversations: 0,
        totalConversations: 0,
        readConversations: 0,
        error: error.message
      }
    }
  }

  static async checkAppointmentMode () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/mode/check', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        appointmentMode: data.appointment_mode || false,
        status: data.status || 'disabled',
        extraInfo: data.extra_info || ''
      }
    } catch (error) {
      console.error('Error checking appointment mode:', error)
      return {
        success: false,
        appointmentMode: false,
        status: 'disabled',
        extraInfo: '',
        error: error.message
      }
    }
  }

  static async toggleAppointmentMode () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/mode/toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Mode toggled',
        appointmentMode: data.appointment_mode || false,
        status: data.status || 'disabled'
      }
    } catch (error) {
      console.error('Error toggling appointment mode:', error)
      return {
        success: false,
        message: 'Failed to toggle mode',
        appointmentMode: false,
        status: 'disabled',
        error: error.message
      }
    }
  }

  static async checkRestaurantMode () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        restaurantMode: data.restaurant_mode || false,
        status: data.status || 'disabled'
      }
    } catch (error) {
      console.error('Error checking restaurant mode:', error)
      return {
        success: false,
        restaurantMode: false,
        status: 'disabled',
        error: error.message
      }
    }
  }

  static async toggleRestaurantMode () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/restaurant/mode/toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Restaurant mode toggled',
        restaurantMode: data.restaurant_mode || false,
        status: data.status || 'disabled'
      }
    } catch (error) {
      console.error('Error toggling restaurant mode:', error)
      return {
        success: false,
        message: 'Failed to toggle restaurant mode',
        restaurantMode: false,
        status: 'disabled',
        error: error.message
      }
    }
  }

  static async getDepartmentModeSettings () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customer-service-department-mode/settings', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)

      return {
        success: data.success || false,
        customer_service_department_mode: data.customer_service_department_mode || 0,
        department_timeout_hours: data.department_timeout_hours || 24,
        department_selection_header_ar: data.department_selection_header_ar || 'اختر القسم',
        department_selection_header_en: data.department_selection_header_en || 'Select Department',
        department_selection_body_ar: data.department_selection_body_ar || 'مرحباً! من فضلك اختر القسم الذي تريد التواصل معه:',
        department_selection_body_en: data.department_selection_body_en || 'Welcome! Please select the department you would like to connect with:',
        department_selection_header_type: data.department_selection_header_type || 'text',
        department_selection_header_media_url: data.department_selection_header_media_url || null
      }
    } catch (error) {
      console.error('Error fetching department mode settings:', error)
      return {
        success: false,
        customer_service_department_mode: 0,
        department_timeout_hours: 24,
        department_selection_header_ar: 'اختر القسم',
        department_selection_header_en: 'Select Department',
        department_selection_body_ar: 'مرحباً! من فضلك اختر القسم الذي تريد التواصل معه:',
        department_selection_body_en: 'Welcome! Please select the department you would like to connect with:',
        department_selection_header_type: 'text',
        department_selection_header_media_url: null,
        error: error.message
      }
    }
  }

  static async updateDepartmentModeSettings (settings) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customer-service-department-mode/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(settings)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Department mode settings updated successfully'
      }
    } catch (error) {
      console.error('Error updating department mode settings:', error)
      return {
        success: false,
        message: 'Failed to update department mode settings',
        error: error.message
      }
    }
  }

  static async uploadDepartmentMedia (file, mediaType) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', mediaType)

      const response = await fetch('https://api.quali-d.com/customer-service-department-mode/upload-media', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Media uploaded successfully',
        media_url: data.media_url,
        media_type: data.media_type,
        full_url: data.full_url
      }
    } catch (error) {
      console.error('Error uploading department media:', error)
      return {
        success: false,
        message: error.message || 'Failed to upload media'
      }
    }
  }

  static async deleteDepartmentMedia () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customer-service-department-mode/delete-media', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Media deleted successfully'
      }
    } catch (error) {
      console.error('Error deleting department media:', error)
      return {
        success: false,
        message: error.message || 'Failed to delete media'
      }
    }
  }

  static async getCompanyProfile () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/get', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        profile: data.profile || {},
        error: null
      }
    } catch (error) {
      console.error('Error fetching company profile:', error)
      return {
        success: false,
        profile: {},
        error: error.message
      }
    }
  }

  static async updateCompanyProfile (profileData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(profileData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Company profile updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error updating company profile:', error)
      return {
        success: false,
        message: 'Failed to update company profile',
        error: error.message
      }
    }
  }

  static async getAvailableLeads () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/get-unselected-leads', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        leads: data.leads || [],
        error: null
      }
    } catch (error) {
      console.error('Error fetching available leads:', error)
      return {
        success: false,
        leads: [],
        error: error.message
      }
    }
  }

  static async getCompanyLeads () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/leads-list', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        companyLeads: data.leads || [],
        error: null
      }
    } catch (error) {
      console.error('Error fetching company leads:', error)
      return {
        success: false,
        companyLeads: [],
        error: error.message
      }
    }
  }

  static async assignLeadToCompany (leadId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/leads-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ lead_id: leadId })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Lead assigned to company successfully',
        error: null
      }
    } catch (error) {
      console.error('Error assigning lead to company:', error)
      return {
        success: false,
        message: 'Failed to assign lead to company',
        error: error.message
      }
    }
  }

  static async removeLeadFromCompany (leadId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/company-profile/delete-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ lead_id: leadId })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Lead removed from company successfully',
        error: null
      }
    } catch (error) {
      console.error('Error removing lead from company:', error)
      return {
        success: false,
        message: 'Failed to remove lead from company',
        error: error.message
      }
    }
  }

  static async getTodos () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/todos', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        todos: data.todos || data.data || [],
        error: null
      }
    } catch (error) {
      console.error('Error fetching todos:', error)
      return {
        success: false,
        todos: [],
        error: error.message
      }
    }
  }

  static async createTodo (todoData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/todos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(todoData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        todo: data.todo || data.data || {},
        message: data.message || 'Todo created successfully',
        error: null
      }
    } catch (error) {
      console.error('Error creating todo:', error)
      return {
        success: false,
        todo: {},
        message: 'Failed to create todo',
        error: error.message
      }
    }
  }

  static async updateTodo (todoId, todoData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/todos/${todoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(todoData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        todo: data.todo || data.data || {},
        message: data.message || 'Todo updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error updating todo:', error)
      return {
        success: false,
        todo: {},
        message: 'Failed to update todo',
        error: error.message
      }
    }
  }

  static async deleteTodo (todoId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/todos/${todoId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Todo deleted successfully',
        error: null
      }
    } catch (error) {
      console.error('Error deleting todo:', error)
      return {
        success: false,
        message: 'Failed to delete todo',
        error: error.message
      }
    }
  }

  static async changeToDoStatus (todoId, status) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/todos/${todoId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ status })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        todo: data.todo || data.data || {},
        message: data.message || 'Todo status updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error changing todo status:', error)
      return {
        success: false,
        todo: {},
        message: 'Failed to update todo status',
        error: error.message
      }
    }
  }

  static async toggleTodoPin (todoId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/todos/${todoId}/pin`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        todo: data.todo || data.data || {},
        message: data.message || 'Todo pin status updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error toggling todo pin:', error)
      return {
        success: false,
        todo: {},
        message: 'Failed to toggle todo pin',
        error: error.message
      }
    }
  }

  static async getCustomers (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.set('page', params.page)
      if (params.per_page) queryParams.set('per_page', params.per_page)
      if (params.phone_number) queryParams.set('phone_number', params.phone_number)
      if (params.category_id) queryParams.set('category_id', params.category_id)
      if (params.vip_only) queryParams.set('vip_only', params.vip_only)
      if (params.search_name) queryParams.set('search_name', params.search_name)
      if (params.engagement) queryParams.set('engagement', params.engagement)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/customers?${queryParams}`
        : 'https://api.quali-d.com/customers'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        customers: data.data?.customers || [],
        pagination: data.data?.pagination || {},
        filters: data.data?.filters || {},
        message: data.message || 'Customers retrieved successfully',
        error: null
      }
    } catch (error) {
      console.error('Error fetching customers:', error)
      return {
        success: false,
        customers: [],
        pagination: {},
        filters: {},
        message: 'Failed to fetch customers',
        error: error.message
      }
    }
  }

  static async getCustomerCategories () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customers-categories', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        categories: data.categories || [],
        message: data.message || 'Categories retrieved successfully',
        error: null
      }
    } catch (error) {
      console.error('Error fetching customer categories:', error)
      return {
        success: false,
        categories: [],
        message: 'Failed to fetch categories',
        error: error.message
      }
    }
  }

  static async createCustomerCategory (name) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customer-category-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ name })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Category created successfully',
        categoryId: data.category_id,
        error: null
      }
    } catch (error) {
      console.error('Error creating customer category:', error)
      return {
        success: false,
        message: 'Failed to create category',
        error: error.message
      }
    }
  }

  static async renameCustomerCategory (categoryId, name) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customer-category-update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          category_id: categoryId,
          name: name
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Category updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error updating customer category:', error)
      return {
        success: false,
        message: 'Failed to update category',
        error: error.message
      }
    }
  }

  static async deleteCustomerCategory (categoryId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/delete-customer-category', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ category_id: categoryId })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Category deleted successfully',
        error: null
      }
    } catch (error) {
      console.error('Error deleting customer category:', error)
      return {
        success: false,
        message: 'Failed to delete category',
        error: error.message
      }
    }
  }

  static async updateCustomerVip (customerId, vipStatus) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customers-vip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          customer_id: customerId,
          vip_status: vipStatus
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'VIP status updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error updating VIP status:', error)
      return {
        success: false,
        message: 'Failed to update VIP status',
        error: error.message
      }
    }
  }

  static async updateCustomerCategory (customerId, categoryId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customers-category', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          customer_id: customerId,
          category_id: categoryId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Customer category updated successfully',
        error: null
      }
    } catch (error) {
      console.error('Error updating customer category:', error)
      return {
        success: false,
        message: 'Failed to update customer category',
        error: error.message
      }
    }
  }

  static async deleteCustomer (customerId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/delete-customer', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          customer_id: customerId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Customer deleted successfully',
        error: null
      }
    } catch (error) {
      console.error('Error deleting customer:', error)
      return {
        success: false,
        message: 'Failed to delete customer',
        error: error.message
      }
    }
  }

  static async updateCustomer (customerId, customerData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const url = `https://api.quali-d.com/customers/${customerId}`
      console.log('Making PUT request to:', url)
      console.log('With data:', customerData)
      console.log('With token:', token ? 'Present' : 'Missing')

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(customerData)
      })

      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)

      if (!response.ok) {
        const errorText = await response.text()
        console.log('Error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
      }

      const data = await this.parseJsonResponse(response)
      console.log('Parsed response data:', data)
      return {
        success: data.success || true,
        message: data.message || 'Customer updated successfully',
        customer: data.customer || {},
        error: null
      }
    } catch (error) {
      console.error('Error updating customer:', error)
      return {
        success: false,
        message: 'Failed to update customer: ' + error.message,
        customer: {},
        error: error.message
      }
    }
  }

  static async addCustomer (customerData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/add-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(customerData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Customer added successfully',
        customer_id: data.customer_id,
        error: null
      }
    } catch (error) {
      console.error('Error adding customer:', error)
      return {
        success: false,
        message: 'Failed to add customer',
        customer: {},
        error: error.message
      }
    }
  }

  static async uploadCustomers (file) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('https://api.quali-d.com/upload-customers', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Customers uploaded successfully',
        results: data.results || {},
        error: null
      }
    } catch (error) {
      console.error('Error uploading customers:', error)
      return {
        success: false,
        message: 'Failed to upload customers',
        results: {},
        error: error.message
      }
    }
  }

  static async exportCustomers (categoryId = null) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const queryParams = new URLSearchParams()
      if (categoryId) {
        queryParams.set('category_id', categoryId)
      }

      const url = queryParams.toString()
        ? `https://api.quali-d.com/export-customers?${queryParams}`
        : 'https://api.quali-d.com/export-customers'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Customers exported successfully',
        url: data.url || '',
        totalCustomers: data.total_customers || 0,
        error: null
      }
    } catch (error) {
      console.error('Error exporting customers:', error)
      return {
        success: false,
        message: 'Failed to export customers',
        url: '',
        totalCustomers: 0,
        error: error.message
      }
    }
  }

  // Products/Services methods
  static async getProductsServices () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/productsservices-getall', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        data: data.data || [],
        headers: data.headers || [],
        total: data.total || 0,
        message: data.message || 'Data retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching products/services:', error)
      return {
        success: false,
        data: [],
        headers: [],
        total: 0,
        message: 'Failed to fetch data',
        error: error.message
      }
    }
  }

  static async uploadProductsServices (file) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('https://api.quali-d.com/productsservices-upload-file', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'File processed successfully'
      }
    } catch (error) {
      console.error('Error uploading products/services file:', error)
      return {
        success: false,
        message: 'Failed to upload file: ' + error.message,
        error: error.message
      }
    }
  }

  static async deleteAllProductsServices () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/productsservices-delete-all', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'All data deleted successfully'
      }
    } catch (error) {
      console.error('Error deleting all products/services:', error)
      return {
        success: false,
        message: 'Failed to delete data: ' + error.message,
        error: error.message
      }
    }
  }

  static async addSingleProductService (productData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/products-services/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(productData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message || 'Product/service added successfully',
        id: data.id,
        data: data.data,
        errors: data.errors,
        error: data.error
      }
    } catch (error) {
      console.error('Error adding product/service:', error)
      return {
        success: false,
        message: 'Failed to add product/service: ' + error.message,
        error: error.message
      }
    }
  }

  static async deleteSingleProductService (productId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/productsservices-delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ id: productId })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'Product deleted successfully'
      }
    } catch (error) {
      console.error('Error deleting product/service:', error)
      return {
        success: false,
        message: 'Failed to delete product/service: ' + error.message,
        error: error.message
      }
    }
  }

  static async aiEnhanceProductService (productId, style) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/products-services/ai-enhance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ id: productId, style })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        message: data.message,
        original_description: data.original_description,
        enhanced_description: data.enhanced_description,
        style: data.style,
        credits_used: data.credits_used,
        remaining_credits: data.remaining_credits
      }
    } catch (error) {
      console.error('Error enhancing product/service with AI:', error)
      return {
        success: false,
        message: 'Failed to enhance description: ' + error.message,
        error: error.message
      }
    }
  }

  static async updateProductService (productId, description, name = null) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const body = { id: productId, description }
      if (name !== null) {
        body.name = name
      }

      const response = await fetch('https://api.quali-d.com/products-services/update-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        message: data.message || 'Product updated successfully'
      }
    } catch (error) {
      console.error('Error updating product/service:', error)
      return {
        success: false,
        message: 'Failed to update product/service: ' + error.message,
        error: error.message
      }
    }
  }

  // Appointments methods
  static async getAppointments (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.set('page', params.page)
      if (params.limit) queryParams.set('limit', params.limit)
      if (params.date) queryParams.set('date', params.date)
      if (params.status) queryParams.set('status', params.status)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/appointments?${queryParams}`
        : 'https://api.quali-d.com/appointments'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        appointments: data.appointments || [],
        pagination: data.pagination || null,
        message: data.message || 'Appointments retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching appointments:', error)
      return {
        success: false,
        appointments: [],
        pagination: null,
        message: 'Failed to fetch appointments',
        error: error.message
      }
    }
  }

  static async getAllDashboardData () {
    try {
      // Fetch all data in parallel for better performance
      const [topCustomerService, topSales, qualiBotStatus, activeAgents] = await Promise.all([
        this.getTopCustomerServiceAgent(),
        this.getTopSalesAgent(),
        this.getQualiBotStatus(),
        this.getActiveAgents()
      ])

      return {
        topCustomerService,
        topSales,
        qualiBotStatus,
        activeAgents,
        lastUpdated: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      return {
        topCustomerService: { bestPerformerName: 'Error' },
        topSales: { bestPerformerName: 'Error' },
        qualiBotStatus: { qualibotEnabled: false, status: 'ERROR', colorCode: '#ef4444' },
        activeAgents: { activeAgentsCount: 0, offlineAgentsCount: 0, totalAgents: 0 },
        error: error.message,
        lastUpdated: new Date().toISOString()
      }
    }
  }

  static async getCalendarLinkStatus () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/calendar-links/status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching calendar link status:', error)
      return {
        success: false,
        message: error.message,
        calendar_links_enabled: false
      }
    }
  }

  static async toggleCalendarLinks () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/calendar-links/toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error toggling calendar links:', error)
      return {
        success: false,
        message: error.message,
        calendar_links_enabled: false
      }
    }
  }

  static async getEscalations (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()

      if (params.page) queryParams.set('page', params.page)
      if (params.per_page) queryParams.set('per_page', params.per_page)
      if (params.agentId) queryParams.set('agent_id', params.agentId)
      if (params.escalationLevel) queryParams.set('escalation_level', params.escalationLevel)
      if (params.category) queryParams.set('category', params.category)
      if (params.dateFrom) queryParams.set('date_from', params.dateFrom)
      if (params.dateTo) queryParams.set('date_to', params.dateTo)
      if (params.search) queryParams.set('search', params.search)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/escalations?${queryParams}`
        : 'https://api.quali-d.com/escalations'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        escalations: data.escalations || [],
        pagination: data.pagination || {},
        filtersApplied: data.filters_applied || {},
        message: 'Escalations retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching escalations:', error)
      return {
        success: false,
        escalations: [],
        pagination: {},
        filtersApplied: {},
        message: 'Failed to fetch escalations',
        error: error.message
      }
    }
  }

  static async getPaymentSettings () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/payment-settings', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching payment settings:', error)
      return {
        success: false,
        message: error.message,
        payment_required: false,
        payment_configured: false
      }
    }
  }

  static async createPaymentSettings (paymentData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/payment-settings', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(paymentData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error creating payment settings:', error)
      return {
        success: false,
        message: error.message
      }
    }
  }

  static async updatePaymentSettings (paymentData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/appointments/payment-config', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          payment_amount: paymentData.payment_amount,
          instapay_account: paymentData.instapay_account,
          instapay_link: paymentData.instapay_link
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error updating payment settings:', error)
      return {
        success: false,
        message: error.message
      }
    }
  }

  static async getAIAnalyses (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()

      if (params.page) queryParams.set('page', params.page)
      if (params.per_page) queryParams.set('per_page', params.per_page)
      if (params.analysisType) queryParams.set('analysis_type', params.analysisType)
      if (params.agentId) queryParams.set('agent_id', params.agentId)
      if (params.satisfaction) queryParams.set('satisfaction', params.satisfaction)
      if (params.resolution) queryParams.set('resolution', params.resolution)
      if (params.dateFrom) queryParams.set('date_from', params.dateFrom)
      if (params.dateTo) queryParams.set('date_to', params.dateTo)
      if (params.minSatisfactionScore) queryParams.set('min_satisfaction_score', params.minSatisfactionScore)
      if (params.maxSatisfactionScore) queryParams.set('max_satisfaction_score', params.maxSatisfactionScore)
      if (params.search) queryParams.set('search', params.search)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/ai-analysis/list?${queryParams}`
        : 'https://api.quali-d.com/ai-analysis/list'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        analyses: data.analyses || [],
        pagination: data.pagination || {},
        summaryStats: data.summary_stats || {},
        filtersApplied: data.filters_applied || {},
        message: 'AI analyses retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching AI analyses:', error)
      return {
        success: false,
        analyses: [],
        pagination: {},
        summaryStats: {},
        filtersApplied: {},
        message: 'Failed to fetch AI analyses',
        error: error.message
      }
    }
  }

  static async getAIAnalysisOverview (params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()

      if (params.date_from) queryParams.set('date_from', params.date_from)
      if (params.date_to) queryParams.set('date_to', params.date_to)
      if (params.analysis_type) queryParams.set('analysis_type', params.analysis_type)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/ai-analysis/overview?${queryParams}`
        : 'https://api.quali-d.com/ai-analysis/overview'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        ...data
      }
    } catch (error) {
      console.error('Error fetching AI analysis overview:', error)
      return {
        success: false,
        overview_period: {},
        overall_statistics: {},
        satisfaction_distribution: [],
        resolution_distribution: [],
        top_performing_agents: [],
        most_common_topics: [],
        most_common_issues: [],
        daily_trends: [],
        risk_analysis: {},
        critical_analyses: [],
        performance_insights: {},
        filters_applied: {},
        generated_at: null,
        message: 'Failed to fetch AI analysis overview',
        error: error.message
      }
    }
  }

  static async getAgentReport (agentId, params = {}) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const queryParams = new URLSearchParams()

      if (params.period) queryParams.set('period', params.period)
      if (params.date_from) queryParams.set('date_from', params.date_from)
      if (params.date_to) queryParams.set('date_to', params.date_to)

      const url = queryParams.toString()
        ? `https://api.quali-d.com/ai-analysis/agent-report/${agentId}?${queryParams}`
        : `https://api.quali-d.com/ai-analysis/agent-report/${agentId}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        ...data
      }
    } catch (error) {
      console.error('Error fetching agent report:', error)
      return {
        success: false,
        message: 'Failed to fetch agent report',
        error: error.message
      }
    }
  }

  static async getUnanalyzedWhatsAppCount () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/unanalyzed-whatsapp', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        totalUnanalyzed: data.total_unanalyzed_conversations || 0,
        message: 'Unanalyzed count retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching unanalyzed WhatsApp count:', error)
      return {
        success: false,
        totalUnanalyzed: 0,
        message: 'Failed to fetch unanalyzed count',
        error: error.message
      }
    }
  }

  static async analyzeAllWhatsAppConversations () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/auto-analyze', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({})
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Auto-analysis initiated successfully',
        data: data
      }
    } catch (error) {
      console.error('Error starting auto-analysis:', error)
      return {
        success: false,
        message: 'Failed to start auto-analysis',
        error: error.message
      }
    }
  }

  static async getTodaysConversations () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/todays-conversations', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        ...data
      }
    } catch (error) {
      console.error('Error fetching today\'s conversations:', error)
      return {
        success: false,
        date: new Date().toISOString().split('T')[0],
        todays_conversations: 0,
        yesterdays_conversations: 0,
        week_conversations: 0,
        month_conversations: 0,
        change_from_yesterday: {
          absolute: 0,
          percentage: 0
        },
        error: error.message
      }
    }
  }

  static async getLeadDashboardStats () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/collected-leads/dashboard-stats', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      const dashboard = data.dashboard || {}

      // Helper function to calculate trend based on completion rate change
      const calculateTrend = (current, previous) => {
        if (!previous || previous === 0) return '+0%'
        const change = current - previous
        const sign = change >= 0 ? '+' : ''
        return `${sign}${change.toFixed(1)}%`
      }

      // Extract trends data for calculations
      const trends = dashboard.trends || {}
      const dailyTrend = trends.daily_trend || []

      // Calculate previous day completion rate for trend
      const yesterdayData = dailyTrend.find(d => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        return d.period === yesterday.toISOString().split('T')[0]
      })

      const todayRate = dashboard.today?.completion_rate || 0
      const yesterdayRate = yesterdayData ? (yesterdayData.completed / (yesterdayData.generated || 1)) * 100 : 0

      return {
        success: true,
        todayStats: {
          count: dashboard.today?.total_completed || 0,
          generated: dashboard.today?.total_generated || 0,
          completion_rate: dashboard.today?.completion_rate || 0,
          trend: calculateTrend(todayRate, yesterdayRate)
        },
        weekStats: {
          count: dashboard.week?.total_completed || 0,
          generated: dashboard.week?.total_generated || 0,
          completion_rate: dashboard.week?.completion_rate || 0,
          trend: `${dashboard.week?.completion_rate?.toFixed(1) || 0}%`
        },
        monthStats: {
          count: dashboard.month?.total_completed || 0,
          generated: dashboard.month?.total_generated || 0,
          completion_rate: dashboard.month?.completion_rate || 0,
          trend: `${dashboard.month?.completion_rate?.toFixed(1) || 0}%`
        },
        allTimeStats: {
          count: dashboard.all_time?.total_completed || 0,
          generated: dashboard.all_time?.total_generated || 0,
          completion_rate: dashboard.all_time?.completion_rate || 0,
          trend: 'Total'
        },
        recentCompletions: dashboard.week?.recent_completions || dashboard.month?.recent_completions || [],
        motivationalMessage: dashboard.today?.motivational_message || dashboard.week?.motivational_message || 'Keep up the great work! 💪',
        trends: trends,
        error: null
      }
    } catch (error) {
      console.error('Error fetching lead dashboard stats:', error)
      return {
        success: false,
        todayStats: { count: 0, generated: 0, completion_rate: 0, trend: '+0%' },
        weekStats: { count: 0, generated: 0, completion_rate: 0, trend: '+0%' },
        monthStats: { count: 0, generated: 0, completion_rate: 0, trend: '+0%' },
        allTimeStats: { count: 0, generated: 0, completion_rate: 0, trend: 'Total' },
        recentCompletions: [],
        motivationalMessage: 'Keep up the great work! 💪',
        error: error.message
      }
    }
  }

  static async getCompletedLeads () {
    try {
      const token = AuthService.getAuthToken()
      console.log('getCompletedLeads - Token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN')

      if (!token) {
        console.error('getCompletedLeads - No token found in localStorage')
        throw new Error('No authentication token found')
      }

      console.log('getCompletedLeads - Making API request to: https://api.quali-d.com/collected-leads?status=complete')

      const response = await fetch('https://api.quali-d.com/collected-leads?status=complete', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)

      return {
        success: true,
        leads: data.leads || [],
        count: data.count || 0,
        error: null
      }
    } catch (error) {
      console.error('Error fetching completed leads:', error)
      return {
        success: false,
        leads: [],
        count: 0,
        error: error.message
      }
    }
  }

  // Quick Replies methods
  static async getQuickReplies () {
    console.log('[DashboardService] getQuickReplies called')
    try {
      const token = AuthService.getAuthToken()
      console.log('[DashboardService] Token:', token ? 'exists' : 'missing')
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('[DashboardService] About to fetch: https://api.quali-d.com/quick-replies')
      const response = await fetch('https://api.quali-d.com/quick-replies', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })
      console.log('[DashboardService] Fetch completed, response status:', response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      console.log('[DashboardService] Parsed data:', data)
      return {
        success: data.success || true,
        quick_replies: data.quick_replies || [],
        count: data.count || 0,
        max_allowed: data.max_allowed || 5,
        error: null
      }
    } catch (error) {
      console.error('[DashboardService] Error fetching quick replies:', error)
      return {
        success: false,
        quick_replies: [],
        count: 0,
        max_allowed: 5,
        error: error.message
      }
    }
  }

  static async createQuickReply (quickReplyData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/quick-replies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(quickReplyData)
      })

      if (!response.ok) {
        const data = await this.parseJsonResponse(response)
        return {
          success: false,
          message: data.message || `HTTP error! status: ${response.status}`,
          error: data.message || 'Failed to create quick reply'
        }
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Quick reply created successfully',
        quick_reply: data.quick_reply || {},
        error: null
      }
    } catch (error) {
      console.error('Error creating quick reply:', error)
      return {
        success: false,
        message: 'Failed to create quick reply',
        error: error.message
      }
    }
  }

  static async updateQuickReply (quickReplyId, quickReplyData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/quick-replies/${quickReplyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(quickReplyData)
      })

      if (!response.ok) {
        const data = await this.parseJsonResponse(response)
        return {
          success: false,
          message: data.message || `HTTP error! status: ${response.status}`,
          error: data.message || 'Failed to update quick reply'
        }
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Quick reply updated successfully',
        quick_reply: data.quick_reply || {},
        error: null
      }
    } catch (error) {
      console.error('Error updating quick reply:', error)
      return {
        success: false,
        message: 'Failed to update quick reply',
        error: error.message
      }
    }
  }

  static async deleteQuickReply (quickReplyId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/quick-replies/${quickReplyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        const data = await this.parseJsonResponse(response)
        return {
          success: false,
          message: data.message || `HTTP error! status: ${response.status}`,
          error: data.message || 'Failed to delete quick reply'
        }
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Quick reply deleted successfully',
        error: null
      }
    } catch (error) {
      console.error('Error deleting quick reply:', error)
      return {
        success: false,
        message: 'Failed to delete quick reply',
        error: error.message
      }
    }
  }

  static async bulkDeleteCustomers (customerIds) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customers/bulk-delete', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_ids: customerIds
        })
      })

      if (!response.ok) {
        const data = await this.parseJsonResponse(response)
        return {
          success: false,
          message: data.message || 'Failed to delete customers',
          error: data.message || 'Failed to delete customers'
        }
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || `${data.deleted_count} customer(s) deleted successfully`,
        deletedCount: data.deleted_count,
        error: null
      }
    } catch (error) {
      console.error('Error bulk deleting customers:', error)
      return {
        success: false,
        message: 'Failed to delete customers',
        error: error.message
      }
    }
  }

  static async bulkChangeCategory (customerIds, categoryId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/customers/bulk-change-category', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_ids: customerIds,
          category_id: categoryId
        })
      })

      if (!response.ok) {
        const data = await this.parseJsonResponse(response)
        return {
          success: false,
          message: data.message || 'Failed to change category',
          error: data.message || 'Failed to change category'
        }
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || `${data.updated_count} customer(s) category updated successfully`,
        updatedCount: data.updated_count,
        error: null
      }
    } catch (error) {
      console.error('Error bulk changing category:', error)
      return {
        success: false,
        message: 'Failed to change category',
        error: error.message
      }
    }
  }

  // ==================== CUSTOM FLOWS ====================

  static async getCustomFlows () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/custom-flows', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        flows: data.flows || data.data || [],
        message: data.message || 'Flows loaded successfully',
        error: null
      }
    } catch (error) {
      console.error('Error fetching custom flows:', error)
      return {
        success: false,
        flows: [],
        message: 'Failed to fetch custom flows',
        error: error.message
      }
    }
  }

  static async getCustomFlow (flowId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/custom-flows/${flowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        flow: data.flow || data.data || {},
        message: data.message || 'Flow loaded successfully',
        error: null
      }
    } catch (error) {
      console.error('Error fetching custom flow:', error)
      return {
        success: false,
        flow: {},
        message: 'Failed to fetch custom flow',
        error: error.message
      }
    }
  }

  static async createCustomFlow (flowData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/custom-flows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(flowData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      const flow = data.flow || data.data || {}
      return {
        success: data.success || true,
        flow: flow,
        flow_id: flow.id || data.flow_id || null,
        message: data.message || 'Flow created successfully',
        error: null
      }
    } catch (error) {
      console.error('Error creating custom flow:', error)
      return {
        success: false,
        flow: {},
        message: 'Failed to create custom flow',
        error: error.message
      }
    }
  }

  static async updateCustomFlow (flowId, flowData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('Calling API to update flow:', flowId)

      const response = await fetch(`https://api.quali-d.com/custom-flows/${flowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(flowData)
      })

      console.log('API response status:', response.status, response.ok)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      console.log('Parsed API data:', data)

      const result = {
        success: data.success || true,
        flow: data.flow || data.data || {},
        message: data.message || 'Flow updated successfully',
        error: null
      }
      console.log('Returning result:', result)
      return result
    } catch (error) {
      console.error('Error updating custom flow:', error)
      const errorResult = {
        success: false,
        flow: {},
        message: 'Failed to update custom flow',
        error: error.message
      }
      console.log('Returning error result:', errorResult)
      return errorResult
    }
  }

  static async deleteCustomFlow (flowId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://api.quali-d.com/custom-flows/${flowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        message: data.message || 'Flow deleted successfully',
        error: null
      }
    } catch (error) {
      console.error('Error deleting custom flow:', error)
      return {
        success: false,
        message: 'Failed to delete custom flow',
        error: error.message
      }
    }
  }

  static async checkCustomFlowMode () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/custom-flows/mode/check', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        customFlowMode: data.custom_flow_mode || false,
        activeFlowId: data.active_flow_id || null,
        activeFlowName: data.active_flow_name || null
      }
    } catch (error) {
      console.error('Error checking custom flow mode:', error)
      return {
        success: false,
        customFlowMode: false,
        activeFlowId: null,
        activeFlowName: null
      }
    }
  }

  static async toggleCustomFlowMode (flowId = null) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/custom-flows/mode/toggle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ flow_id: flowId })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || false,
        customFlowMode: data.custom_flow_mode || false,
        activeFlowId: data.active_flow_id || null,
        message: data.message || 'Custom flow mode toggled'
      }
    } catch (error) {
      console.error('Error toggling custom flow mode:', error)
      return {
        success: false,
        customFlowMode: false,
        activeFlowId: null,
        message: 'Failed to toggle custom flow mode'
      }
    }
  }

  // ==================== GOOGLE INTEGRATIONS ====================

  static async getGoogleConnections () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const response = await fetch('https://api.quali-d.com/google/connections', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, connections: data.connections || [] }
    } catch (error) {
      return { success: false, connections: [] }
    }
  }

  static async disconnectGoogleConnection (connectionId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const response = await fetch(`https://api.quali-d.com/google/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, message: data.message || '' }
    } catch (error) {
      return { success: false, message: error.message }
    }
  }

  static getGoogleOAuthUrl () {
    const token = AuthService.getAuthToken()
    // Pass the frontend origin so the PHP callback knows where to redirect back
    const origin = encodeURIComponent(window.location.origin)
    return `https://api.quali-d.com/google/oauth/redirect?token=${encodeURIComponent(token)}&origin=${origin}`
  }

  static async getGoogleSpreadsheets (connectionId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const response = await fetch(`https://api.quali-d.com/google/spreadsheets?connection_id=${connectionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, spreadsheets: data.spreadsheets || [] }
    } catch (error) {
      return { success: false, spreadsheets: [] }
    }
  }

  static async getGoogleSheets (connectionId, spreadsheetId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const response = await fetch(`https://api.quali-d.com/google/spreadsheets/${spreadsheetId}/sheets?connection_id=${connectionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, sheets: data.sheets || [] }
    } catch (error) {
      return { success: false, sheets: [] }
    }
  }

  static async getGoogleSheetHeaders (connectionId, spreadsheetId, sheetName) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const params = `connection_id=${connectionId}&sheet=${encodeURIComponent(sheetName)}`
      const response = await fetch(`https://api.quali-d.com/google/spreadsheets/${spreadsheetId}/headers?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, headers: data.headers || [] }
    } catch (error) {
      return { success: false, headers: [] }
    }
  }

  static async getGoogleDriveFolders (connectionId) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) throw new Error('No authentication token found')
      const response = await fetch(`https://api.quali-d.com/google/drive/folders?connection_id=${connectionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      const data = await this.parseJsonResponse(response)
      return { success: data.success || false, folders: data.folders || [] }
    } catch (error) {
      return { success: false, folders: [] }
    }
  }
}

export default DashboardService
